import React from 'react';

import { TitleWithDescription, Button } from '@infoods/lifebase-ui';

import { useFormContext } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';

import classes from './BannersStep.module.scss';
import { STEP_FORM_ID } from '../../GuidedChallengeCreation.settings';
import {
  CHALLENGE_BANNER_UPLOAD_TYPE,
  ChallengeBannerUploadType,
} from '../../../../../../constants/challengeBannerUploadType';
import { ChallengeFormValues } from '../../../../../../interfaces/ChallengeFormValues';
import { openModal } from '../../../../../../../shared/utils/reactUtils/openModal/openModal';
import { UploadBannerModal } from '../../../../../../components/form-sections/UploadBannerModal/UploadBannerModal';

type Props = {
  onNext: () => void;
};

const BannersStep = ({ onNext }: Props) => {
  const { setValue, getValues } = useFormContext<ChallengeFormValues>();

  const handleUploadBanner = (bannerType: ChallengeBannerUploadType) => {
    const keyConfig = {
      [CHALLENGE_BANNER_UPLOAD_TYPE.WEB]: 'banners.webBanner',
      [CHALLENGE_BANNER_UPLOAD_TYPE.MOBILE]: 'banners.mobileBanner',
    } as const;

    const formKey = keyConfig[bannerType];

    openModal((closeModal) => (
      <UploadBannerModal
        bannerType={bannerType}
        onClose={closeModal}
        onSave={async (banner) => setValue(formKey, banner)}
        onRemove={async () => setValue(formKey, null)}
        getImage={async () => getValues(formKey)}
      />
    ));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const webBanner = getValues('banners.webBanner');
    const mobileBanner = getValues('banners.mobileBanner');

    if (!webBanner || !mobileBanner) {
      enqueueSnackbar(
        "You need to upload both banners to proceed to the next step. If you can't upload banners now, you can skip this step and add them later.",
        {
          variant: 'error',
          autoHideDuration: 5000,
        },
      );
      return;
    }

    onNext();
  };

  return (
    <form id={STEP_FORM_ID} onSubmit={onSubmit} noValidate className={classes.box}>
      <div className={classes.row}>
        <TitleWithDescription
          title="Web"
          description={
            <span>
              This will be the main picture to be displayed on your challenge site.
              <br />
              Recommended size: 948 x 380 pixels
            </span>
          }
          titleFont="title-16-bold"
        />
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => handleUploadBanner(CHALLENGE_BANNER_UPLOAD_TYPE.WEB)}
        >
          Upload Banner for Web
        </Button>
      </div>
      <div className={classes.row}>
        <TitleWithDescription
          title="Mobile"
          description={
            <span>
              This will be the main picture to be displayed on your challenge site.
              <br />
              Recommended size: 343 x 158 pixels
            </span>
          }
          titleFont="title-16-bold"
        />
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => handleUploadBanner(CHALLENGE_BANNER_UPLOAD_TYPE.MOBILE)}
        >
          Upload Banner for Mobile
        </Button>
      </div>
    </form>
  );
};

export { BannersStep };
